import { ActionInputField } from '@/services/clubCapabilities'

type FieldValue = string | boolean

type Props = {
  field: ActionInputField
  value: FieldValue
  onChange: (value: FieldValue) => void
  error?: string
}

const inputClass =
  'w-full rounded border border-gray-300 px-2.5 py-2 text-sm text-gray-900 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100'

/**
 * Renders one capability input from its typed schema. Switching over the closed
 * `kind` union keeps the form fully data-driven: a new capability ships its
 * fields and this renders them with no feature-specific code.
 */
export const CapabilityFieldInput = ({ field, value, onChange, error }: Props) => {
  const { type } = field
  const id = `cap-field-${field.key}`

  const renderControl = () => {
    switch (type.kind) {
      case 'decimal': {
        const step = type.scale ? Math.pow(10, -type.scale) : 'any'
        return (
          <input
            id={id}
            type="number"
            inputMode="decimal"
            step={step}
            min={type.min}
            max={type.max}
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            className={inputClass}
          />
        )
      }
      case 'int':
        return (
          <input
            id={id}
            type="number"
            step={1}
            min={type.min}
            max={type.max}
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            className={inputClass}
          />
        )
      case 'boolean':
        return (
          <label className="inline-flex items-center gap-x-2 text-sm text-gray-900">
            <input
              id={id}
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{field.description || field.label}</span>
          </label>
        )
      case 'enum':
        return (
          <select id={id} value={value as string} onChange={(e) => onChange(e.target.value)} className={inputClass}>
            <option value="">Select…</option>
            {type.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )
      case 'text':
        return (
          <textarea
            id={id}
            rows={3}
            maxLength={type.maxLength}
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputClass} resize-none`}
          />
        )
      case 'url':
        return (
          <input
            id={id}
            type="url"
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        )
    }
  }

  return (
    <div className="flex flex-col gap-y-1">
      {type.kind !== 'boolean' && (
        <label htmlFor={id} className="text-xs font-semibold text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </label>
      )}
      {type.kind !== 'boolean' && field.description && <p className="text-xs text-gray-400">{field.description}</p>}
      {renderControl()}
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  )
}
